import React, { useState } from "react";
import { Text, ScrollView, Image, TouchableOpacity } from "react-native";
import { router, Href } from "expo-router";
import { useDispatch, useSelector } from "react-redux";
import AsyncStorage from "@react-native-async-storage/async-storage";
import i18n, { availableLocales } from "../../locales/i18n";
import WebSidebar from "./WebSidebar";
import { RootState } from "../../store";
import { appActions, selectIsRTL } from "../../store/slices/appSlice";
import { authActions } from "../../store/slices/authSlice";

// Import settings image
import SettingsImage from "../../assets/images/settings.png";

const WebSettingsScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { sellWithScanner, locale } = useSelector(
    (state: RootState) => state.app,
  );

  // Use Redux selector for RTL - single source of truth
  const isRTL = useSelector(selectIsRTL);

  const [showLanguages, setShowLanguages] = useState(false);
  const [showSalesMethod, setShowSalesMethod] = useState(false);

  // RTL direction is now handled by WebSidebar component

  const handleNavigateToAccount = () => {
    router.push("/(drawer)/params/profile" as any);
  };

  const handleNavigateToStoreInfo = () => {
    router.push("/(drawer)/params/store-info" as any);
  };

  const handleNavigateToContact = () => {
    router.push("/(drawer)/params/contact" as any);
  };

  const handleNavigateToSubscription = () => {
    router.push("/(drawer)/params/limits" as any);
  };

  const handleToggleSalesMethod = () => {
    setShowSalesMethod(!showSalesMethod);
  };

  const handleSalesMethodChange = (withScanner: boolean) => {
    if (sellWithScanner !== withScanner) {
      dispatch(appActions.toggleSellWithScanner());
    }
    setShowSalesMethod(false);
  };

  const handleToggleLanguages = () => {
    setShowLanguages(!showLanguages);
  };

  const handleLanguageChange = async (lang: string) => {
    try {
      // Update i18n and Redux state
      i18n.locale = lang;
      dispatch(appActions.setLocale(lang));
      setShowLanguages(false);

      // Save to localStorage (for sync read on web) and AsyncStorage
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem("appLocale", lang);
      }
      await AsyncStorage.setItem("appLocale", lang);

      // No reload needed - Redux state change triggers re-render
    } catch (error) {
      // silently ignore
    }
  };

  const handleLogout = async () => {
    try {
      // Clear tokens
      await AsyncStorage.removeItem("accessToken");
      // Clear user state
      dispatch(authActions.setUser(null));
      dispatch(authActions.setAuthError(""));
      // Navigate to login
      router.replace("/(routes)/(auth)/login" as Href);
    } catch (error) {
      // silently ignore
    }
  };

  const settingsItems = [
    {
      id: "account",
      title: i18n.t("comptepersonel"),
      icon: "👤",
      onPress: handleNavigateToAccount,
      hasArrow: true,
    },
    {
      id: "store-info",
      title: i18n.t("storeInfo"),
      icon: "🏪",
      onPress: handleNavigateToStoreInfo,
      hasArrow: true,
    },
    {
      id: "contact",
      title: i18n.t("contactus"),
      icon: "✉️",
      onPress: handleNavigateToContact,
      hasArrow: true,
    },
    {
      id: "subscription",
      title: i18n.t("limits"),
      icon: "⚙️",
      onPress: handleNavigateToSubscription,
      hasArrow: true,
    },
  ];

  return (
    <div
      key={`settings-container-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <div className="flex-1">
        <ScrollView className="flex-1 p-5" showsVerticalScrollIndicator={false}>
          {/* Header with blue bar */}
          <div className="mb-10">
            <div
              className={`flex flex-row items-center gap-4 py-4 px-6 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <div className="w-[100px] h-[100px] rounded-full bg-[#E8F4F8] border-4 border-[#1379A2] items-center justify-center overflow-hidden flex">
                <Image
                  source={SettingsImage}
                  style={{ width: 70, height: 70 }}
                  resizeMode="contain"
                />
              </div>
              <Text className="text-3xl font-bold text-gray-800">
                {i18n.t("settings")}
              </Text>
            </div>
            <div className="h-2 bg-[#1379A2] rounded mt-2" />
          </div>

          {/* Settings List */}
          <div className="items-center gap-4 px-10 flex flex-col">
            {settingsItems.map((item) => (
              <TouchableOpacity
                key={item.id}
                className="flex flex-row items-center bg-white rounded-[30px] py-4 px-6 w-full max-w-[500px] shadow-sm"
                onPress={item.onPress}
              >
                <Text className="text-[28px] text-gray-600 mr-3">‹</Text>
                <div className="flex-1 flex flex-row items-center justify-end gap-3">
                  <Text className="text-lg text-gray-800 font-medium">
                    {item.title}
                  </Text>
                  <Text className="text-[28px]">{item.icon}</Text>
                </div>
              </TouchableOpacity>
            ))}
          </div>

          {/* Language Dropdown */}
          <div className="items-center gap-4 px-10 mt-6 flex flex-col">
            <TouchableOpacity
              className="flex flex-row items-center bg-white rounded-[30px] py-4 px-6 w-full max-w-[500px] shadow-sm"
              onPress={handleToggleLanguages}
            >
              <Text className="text-xl text-gray-600 mr-3">
                {showLanguages ? "⌃" : "⌄"}
              </Text>
              <div className="flex-1 flex flex-row items-center justify-end gap-3">
                <Text className="text-lg text-gray-800 font-medium">
                  {i18n.t("language")} -{" "}
                  {availableLocales.find((l) => l.code === locale)?.name ||
                    locale}
                </Text>
                <Text className="text-[28px]">🌐</Text>
              </div>
            </TouchableOpacity>

            {showLanguages && (
              <div className="bg-white rounded-2xl mt-2 overflow-hidden shadow-sm w-full max-w-[500px]">
                {availableLocales.map((lang) => (
                  <TouchableOpacity
                    key={lang.code}
                    className={`py-3.5 px-6 border-b border-gray-100 ${locale === lang.code ? "bg-[#E8F4F8]" : ""}`}
                    onPress={() => handleLanguageChange(lang.code)}
                  >
                    <Text className="text-lg text-gray-800 text-right">
                      {lang.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </div>
            )}
          </div>

          {/* Logout Button */}
          <div className="items-center px-10 mt-10 mb-10 flex flex-col">
            <TouchableOpacity
              className="flex flex-row items-center justify-center bg-red-500 rounded-[30px] py-4 px-8 w-full max-w-[500px] gap-3 shadow-sm"
              onPress={handleLogout}
            >
              <Text className="text-white text-lg font-semibold">
                {i18n.t("logout")}
              </Text>
            </TouchableOpacity>
          </div>
        </ScrollView>
      </div>
    </div>
  );
};

export default WebSettingsScreen;
